// /redux/slices/user/ledLampsSlice.js
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../../utils/api'; // Your configured HTTP client (e.g., axios instance)

// Async thunk to fetch LED Lamps products from your Django backend
export const fetchLedLamps = createAsyncThunk(
  'ledLamps/fetchLedLamps',
  async (_, { rejectWithValue }) => {
    try {
      // Adjust this endpoint URL to match your backend (e.g., /api/led-lamps/)
      const response = await api.get('/api/led-lamps/');
      return response.data; // Expect an array of product objects
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch LED Lamps products');
    }
  }
);

// Initial filter state matching the component's defaults
const initialFilters = {
  personalize: false,
  productTypes: { hoodies: false, sweatshirts: false },
  sizes: { XS: false, S: false, M: false, L: false, XL: false, '2XL': false, '3XL': false },
  colors: { black: false, green: false, yellow: false, red: false, purple: false, custom: false },
  fabric: { cotton: false, polyester: false, cottonPolyBlend: false },
  printMethod: { screenPrinting: false, dtg: false, dtf: false, embroidery: false, heatTransfer: false },
  printPlacement: { front: false, back: false, sleeve: false, hood: false },
  customization: { oneColor: false, fullColor: false, logoOnly: false },
  orientation: '',
  corners: '',
  designStyle: { inter: false, prata: false, protestRevolution: false, promo: false },
  priceRange: 100,
  rates: { five: false, four: false, three: false },
};

// The initial state starts with empty products as they will be fetched from the backend
const initialState = {
  loading: false,
  error: null,
  products: [],
  filteredProducts: [],
  filters: initialFilters,
};

const ledLampsSlice = createSlice({
  name: 'ledLamps',
  initialState,
  reducers: {
    // Updates a top-level (boolean) filter (e.g., "personalize")
    setSimpleFilter(state, action) {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    // Updates a nested filter property (e.g., productTypes, sizes, colors, etc.)
    setNestedFilter(state, action) {
      const { category, key, value } = action.payload;
      state.filters[category][key] = value;
    },
    // Updates a radio filter (for orientation, corners, etc.)
    setRadioFilter(state, action) {
      const { key, value } = action.payload;
      state.filters[key] = value;
    },
    // Updates the range filter (price)
    setRangeFilter(state, action) {
      state.filters.priceRange = action.payload;
    },
    // Resets all filters to the default state and displays all products
    resetFilters(state) {
      state.filters = { ...initialFilters };
      state.filteredProducts = state.products;
    },
    // Applies filtering logic based on the current filters
    applyFilters(state) {
      const { filters, products } = state;
      
      const selectedTypes = Object.entries(filters.productTypes)
        .filter(([_, value]) => value)
        .map(([key]) => key);
  
      const selectedSizes = Object.entries(filters.sizes)
        .filter(([_, value]) => value)
        .map(([key]) => key);
  
      const selectedRates = Object.entries(filters.rates)
        .filter(([_, value]) => value)
        .map(([key]) => {
          if (key === 'five') return 5;
          if (key === 'four') return 4;
          if (key === 'three') return 3;
          return null;
        })
        .filter((rate) => rate !== null);
  
      state.filteredProducts = products.filter((product) => {
        // If any product type is selected, map product.type to the filter key.
        if (selectedTypes.length > 0) {
          const prodTypeKey = product.type === 'hoodie' ? 'hoodies' : 'sweatshirts';
          if (!selectedTypes.includes(prodTypeKey)) return false;
        }
        // Filter by sizes if any are selected
        if (selectedSizes.length > 0) {
          if (!product.sizes.some((size) => selectedSizes.includes(size))) return false;
        }
        // Price range filter
        if (product.price > filters.priceRange) return false;
        // Rating filter
        if (selectedRates.length > 0) {
          if (!selectedRates.includes(product.rating)) return false;
        }
        // Additional filters (e.g., colors, fabric) can be integrated similarly
        return true;
      });
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchLedLamps.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLedLamps.fulfilled, (state, action) => {
        state.loading = false;
        state.products = action.payload;
        state.filteredProducts = action.payload;
      })
      .addCase(fetchLedLamps.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const {
  setSimpleFilter,
  setNestedFilter,
  setRadioFilter,
  setRangeFilter,
  resetFilters,
  applyFilters,
} = ledLampsSlice.actions;

export default ledLampsSlice.reducer;
